import React, {
    ButtonHTMLAttributes,
    forwardRef,
    MouseEvent,
    ReactNode,
    useCallback,
    useRef,
} from "react";
import classnames from "classnames";

import { useRouter } from "../../context/RouterContext";
import styles from "./Button.module.scss";

export interface IButtonProps<T = any> {
    id?: string;
    to?: string;
    target?: string;
    className?: string;
    activeClassName?: string;
    onClicked?: (context: T) => Promise<void> | void;
    returnMouseEventByDefault?: boolean;
    context?: T;
    children?: ReactNode;
}

export type ButtonProps = IButtonProps & ButtonHTMLAttributes<HTMLElement>;

export const Button = forwardRef<
    HTMLButtonElement & HTMLAnchorElement,
    ButtonProps
>(function Button(props, ref) {
    const {
        id,
        to,
        children,
        className,
        activeClassName,
        returnMouseEventByDefault,
        target,
        context,
        onClicked,
        ...rest
    } = props;

    const loading = useRef(false);
    const { navigate } = useRouter();

    const onClick = useCallback(
        async (event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();

            if (
                to &&
                (to.startsWith("https://") ||
                    to.startsWith("mailto:") ||
                    target === "_blank" ||
                    event.metaKey ||
                    event.ctrlKey)
            ) {
                return;
            }

            event.preventDefault();

            if (onClicked) {
                if (loading.current) {
                    return;
                }
                loading.current = true;
                try {
                    if (
                        (context === undefined || context === null) &&
                        returnMouseEventByDefault
                    ) {
                        await onClicked(event);
                    } else {
                        await onClicked(context);
                    }
                } finally {
                    loading.current = false;
                }
            }

            if (to) {
                await navigate(to);
            }
        },
        [loading, to, target, context, onClicked, navigate],
    );

    const active = (to && window.location.pathname === to) || false;
    const classNames = classnames(
        styles.button,
        className,
        active && activeClassName,
    );

    return (
        (to && (
            <a
                id={id}
                href={to}
                target={target}
                ref={ref}
                onClick={onClick}
                {...rest}
                className={classNames}
            >
                {children}
            </a>
        )) || (
            <button
                id={id}
                onClick={onClicked && onClick}
                ref={ref}
                {...rest}
                className={classNames}
            >
                {children}
            </button>
        )
    );
});

Button.displayName = "Button";
